import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  User,
  TrendingUp,
  Award,
  Target,
  CheckCircle,
  ArrowLeft,
  Video,
  BarChart2,
  Search,
  Plus,
  Filter,
  SortAsc,
  ChevronDown,
  ChevronUp,
  GridIcon,
  List,
  RefreshCw,
  MessageSquare,
} from "lucide-react";
import VideoAnalyticsModal from "./VideoAnalyticsModal";
import samplePlayersData from "./player_profiles/sample_players.json";
import PlayerChatbot from "./player_profiles/PlayerChatbot";
import TeamRosterToggle from "./TeamRosterToggle";
import apiClient from "../utils/apiClient";
import { getSelectedSport, getSportTitle, isCBB } from "../utils/helpers";
import { useTheme } from "../utils/theme";

// Sample player data - updated to include all formats
export const samplePlayers = [
  // Charlie Saum - College player format
  {
    SLAM_Score: 74,
    personal_info: {
      name: "Charlie Saum",
      image:
        "https://gostanford.com/imgproxy/znChkyvn4JNeJ-4_QH1Pr46E4fZ2vafjdhT9brZ8sCE/rs:fit:1980:0:0/g:ce/q:90/aHR0cHM6Ly9zdG9yYWdlLmdvb2dsZWFwaXMuY29tL3N0YW5mb3JkLXByb2QvMjAyNC8wNi8yNC9MWVI5Q2YyT1pWZGpRbmZuQ3BkNXRoNnJnQXJSQlVtNjRXNG5qbzRLLmpwZw.jpg",
      sport: "Baseball",
      position: "Catcher",
      year: "Senior",
      high_school: "Thousand Oaks High School",
      hometown: "Thousand Oaks, California",
      current_team: "Stanford",
      previous_team: "",
      family_legacy: {
        has_family_in_sports: true,
        notable_family_members: [
          "Jake Saum (brother) - played baseball at Tulane and UCLA",
        ],
        legacy_impact_factor: null,
      },
      academic_profile: {
        gpa: null,
        major: "Undisclosed",
        academic_achievements: [
          "Two-time Pac-12 Academic Honor Roll (2023-24)",
          "Maintained academic eligibility at Stanford, one of the most academically demanding universities in the country",
        ],
        academic_eligibility_risk: "low",
      },
    },
    athletic_performance: {
      physical_metrics: {
        height: "6'0\"",
        weight: 195,
        age: "22",
        bats_throws: "R/R",
        fastball_velocity: null,
        fastball_percentile: null,
      },
      career_stats: {
        batting: {
          current_calendar_year: {
            games: 39,
            games_started: null,
            at_bats: null,
            runs: null,
            hits: null,
            doubles: null,
            triples: null,
            home_runs: 4,
            rbis: 18,
            batting_average: 0.226,
            on_base_percentage: 0.344,
            slugging_percentage: 0.361,
            ops: 0.705,
            walks: null,
            strikeouts: null,
            stolen_bases: null,
            caught_stealing: null,
          },
          progression: {
            batting_avg_trend: "Career average of .239",
            power_trend: "Exit velocity: 91 mph",
            plate_discipline: "Walk rate: 12.3%, Strikeout rate: 36%",
            caught_stealing_percentage: "40",
          },
        },
        pitching: {
          current_calendar_year: {
            games: 1,
            innings_pitched: 0.1,
            wins: null,
            losses: null,
            saves: null,
            era: 0,
            strikeouts: 1,
            walks: null,
            whip: null,
            batting_avg_against: null,
          },
          progression: {
            velocity_trend: "",
            command_trend: "",
          },
        },
      },
      key_performance_metrics: [
        "2025 Stats: .226 AVG, .344 OBP, .361 SLG, 4 HR, 18 RBI in 39 games",
        "2024 Stats: .283 AVG, .355 OBP, .545 SLG, 8 HR, 22 RBI in 32 games",
        "Hit safely in six of his last seven games in 2024 season, and 14 of his last 17",
        "Launched a pair of home runs as part of a 2-for-3 day with two runs scored and four RBIs at Texas Tech (April 2024)",
        "Named to Buster Posey Award Watch List (2025)",
        "Strong walk rate (12.3%) reflecting advanced plate discipline",
        "Above-average defensive metrics with 1.89-second pop time (Perfect Game)",
      ],
      national_rankings: {
        overall: 138,
        position: 12,
      },
      awards_recognitions: [
        "Two-time Pac-12 team champion (2022-23)",
        "Two-time Pac-12 Academic Honor Roll (2023-24)",
        "Perfect Game Preseason All-American: 2021 CA Region",
        "Midseason watch list for the Buster Posey Award (2025)",
        "Team USA U-15 (2018) and U-17 (2019) national team member",
        "U15 Baseball World Cup participant (2018)",
        "17u National Team Development program (2019)",
        "All-State and All-County first team in high school (2021)",
        "Two-time All-County first team (2019, 2021)",
        "Tri Valley League MVP (2019)",
        "CIF Southern Section Championship winner at Thousand Oaks High School",
      ],
      injury_history: ["Limited to nine games in 2023 season due to injury"],
      playing_style:
        "Defense-first catcher with leadership qualities and game-calling ability. Strong arm behind the plate with solid receiving skills. Right-handed hitter with developing power but above-average plate discipline (high walk rate). Higher strikeout rate is offset by ability to reach base and provide occasional power.",
      pro_potential: {
        draft_projection: "Late round (10-20) to undrafted free agent",
        professional_readiness: 6,
      },
    },
    marketability_metrics: {
      social_media: {
        platforms: [
          {
            platform: "instagram",
            username: "@c.saum13",
            followers: "Moderate (exact count unavailable)",
            engagement_rate: "Unknown",
            content_quality: 7,
            posting_frequency: 5,
            audience_demographics: {
              primary:
                "Baseball fans, Stanford community, Southern California followers",
            },
          },
        ],
        total_social_reach: "Moderate",
        social_media_growth_rate: "Steady",
        social_media_valuation: "$1,000-5,000 estimated annual potential",
      },
      personal_brand: {
        public_persona:
          "Academic student-athlete at prestigious Stanford program; strong defensive catcher with leadership qualities",
        media_training: true,
        public_speaking_ability: 7,
        interview_performance: 7,
        personal_narrative:
          "California native and defensive-minded catcher who has developed into a power threat at the plate, playing at a high-academic institution while earning national recognition. Former Team USA player with strong leadership qualities.",
        marketable_traits: [
          "Stanford education (prestigious academic institution)",
          "Strong defensive skills at premium position (Catcher)",
          "Team leader and baseball intellect",
          "National award recognition (Buster Posey Award Watch List)",
          "Former USA Baseball National Team Member",
          "Pac-12 Champion",
          "Clean public image and academic excellence",
        ],
        brand_compatibility: [
          "Baseball equipment companies (especially catching gear)",
          "Academic/educational services",
          "Performance training facilities",
          "Local California businesses",
          "Stanford-affiliated organizations",
        ],
        existing_fanbase: {
          size: "Medium (Stanford baseball community, local Thousand Oaks support)",
          loyalty: 8,
          demographics: {
            primary: "Stanford alumni, baseball fans, college sports followers",
            secondary: "",
          },
        },
      },
      media_presence: {
        media_appearances: [
          "Stanford Baseball media coverage",
          "Local California press mentions",
          "College baseball publications and websites",
        ],
        press_coverage: {
          positive: 7,
          neutral: 3,
          negative: null,
        },
        search_volume: "Low to moderate",
        media_perception:
          "Respected collegiate catcher with strong defensive reputation and academic background at Stanford. Viewed as a leader and baseball intellect with potential coaching future.",
      },
    },
    nil_potential_assessment: {
      projected_annual_nil_value: "$5,000-15,000",
    },
  },
  {
    SLAM_Score: 88,
    personal_info: {
      name: "Noah Blythe",
      image: "https://hpusharks.com/images/2024/9/18/IMG_9123.JPG?width=146",
      sport: "Baseball",
      position: "OF/1B",
      year: "Senior",
      high_school: "Valencia High School",
      hometown: "Santa Clarita, CA",
      current_team: "Hawaii Pacific University",
      previous_team: "University of Antelope Valley",
      family_legacy: {
        has_family_in_sports: false,
        notable_family_members: [],
        legacy_impact_factor: 0,
      },
      academic_profile: {
        gpa: 3.4,
        major: "Business Administration",
        academic_achievements: ["Dean's List Fall 2024"],
        academic_eligibility_risk: "Low",
      },
    },
    athletic_performance: {
      physical_metrics: {
        height: "6'3\"",
        weight: 220,
        age: "",
        bats_throws: "Right",
        fastball_velocity: null,
        fastball_percentile: null,
      },
      career_stats: {
        batting: {
          current_calendar_year: {
            games: 45,
            games_started: null,
            at_bats: 157,
            runs: null,
            hits: 81,
            doubles: null,
            triples: null,
            home_runs: 6,
            rbis: 35,
            batting_average: 0.516,
            on_base_percentage: 0.562,
            slugging_percentage: 0.815,
            ops: 1.377,
            walks: null,
            strikeouts: null,
            stolen_bases: null,
            caught_stealing: null,
          },
          progression: {
            batting_avg_trend: "Increasing",
            power_trend: "Stable with occasional power surges",
            plate_discipline: "Excellent",
          },
        },
        pitching: {
          current_calendar_year: {
            games: null,
            innings_pitched: null,
            wins: null,
            losses: null,
            saves: null,
            era: null,
            strikeouts: null,
            walks: null,
            whip: null,
            batting_avg_against: null,
          },
          progression: {
            velocity_trend: "",
            command_trend: "",
          },
        },
      },
      key_performance_metrics: [
        "Elite contact hitter with power",
        "Clutch performance in high-leverage situations",
        "Positional versatility between outfield and first base",
        "Consistent improvement in all offensive categories year over year",
      ],
      national_rankings: {
        overall: null,
        position: 12,
      },
      awards_recognitions: [
        "PacWest All-Conference First Team (2024)",
        "NCBWA West Region Player of the Week (March 10, 2025)",
        "Preseason All-American Honorable Mention (D2CCA, 2025)",
      ],
      injury_history: [],
      playing_style:
        "Power-hitting outfielder with excellent plate discipline and ability to drive the ball to all fields",
      pro_potential: {
        draft_projection: "Late rounds prospect",
        professional_readiness: 7.8,
      },
    },
    marketability_metrics: {
      social_media: {
        platforms: [
          {
            platform: "Instagram",
            username: "noah_blythe20",
            followers: 2800,
            engagement_rate: 9.2,
            content_quality: 8,
            posting_frequency: 7,
            audience_demographics: {
              age_18_24: 58,
              age_25_34: 31,
              regional_followers: "Hawaii and California primarily",
            },
          },
          {
            platform: "Twitter",
            username: "NoahBlythe20",
            followers: 1950,
            engagement_rate: 6.8,
            content_quality: 7,
            posting_frequency: 5,
            audience_demographics: {
              age_18_24: 52,
              age_25_34: 35,
              regional_followers:
                "Hawaii, California, and baseball fans nationally",
            },
          },
        ],
        total_social_reach: 4750,
        social_media_growth_rate: 28.5,
        social_media_valuation: 8500,
      },
      personal_brand: {
        public_persona:
          "Confident, team-oriented power hitter with California roots and Hawaiian spirit",
        media_training: true,
        public_speaking_ability: 8,
        interview_performance: 8,
        personal_narrative:
          "California native who found success after transferring to Hawaii, embracing island culture while maintaining elite performance",
        marketable_traits: [
          "Consistent high performer",
          "Clutch player (walk-off grand slam)",
          "Charismatic personality",
          "Physical presence (6'3\", 220 lbs)",
        ],
        brand_compatibility: [
          "Sports apparel",
          "Fitness products",
          "Hawaii tourism",
          "Baseball equipment",
        ],
        existing_fanbase: {
          size: 5500,
          loyalty: 9,
          demographics: {
            primary: "Hawaii Pacific University community",
            secondary: "California baseball fans and D2 baseball followers",
          },
        },
      },
      media_presence: {
        media_appearances: [
          "HPU Athletics spotlight features",
          "PacWest Conference weekly shows",
          "Local Hawaii sports broadcasts",
          "Santa Clarita hometown newspaper features",
        ],
        press_coverage: {
          positive: 28,
          neutral: 6,
          negative: 0,
        },
        search_volume: 850,
        media_perception:
          "Rising star in D2 baseball with professional potential and fan-friendly personality",
      },
    },
    current_nil_portfolio: {
      existing_deals: [
        {
          brand: "Island Athletic Apparel",
          deal_type: "Endorsement",
          deal_value: 3500,
          term_length: "1 year",
          exclusivity: false,
          performance_metrics: {
            social_posts: 12,
            "in-person_appearances": 4,
          },
        },
        {
          brand: "Local Hawaii Baseball Camp",
          deal_type: "Instruction/Appearance",
          deal_value: 2200,
          term_length: "6 months",
          exclusivity: false,
          performance_metrics: {
            camp_sessions: 8,
            promotional_content: 6,
          },
        },
        {
          brand: "Diamond Sports Equipment",
          deal_type: "Product Sponsorship",
          deal_value: 1800,
          term_length: "1 year",
          exclusivity: true,
          performance_metrics: {
            social_posts: 8,
            testimonials: 2,
          },
        },
      ],
      total_nil_earnings: 7500,
      deal_execution_history: 97,
      category_exclusivity: ["Baseball equipment"],
      entrepreneurial_ventures: [
        "Co-founder of college athlete mentorship program",
      ],
    },
    background_factors: {
      geographic_relevance: {
        regional_popularity: 9,
        local_market_opportunities: [
          "Hawaii tourism partnerships",
          "Local Hawaiian businesses",
          "California hometown connections",
          "Pacific region baseball academies",
        ],
        home_state_influence: 7,
      },
      socioeconomic_profile: {
        background: "Middle class",
        financial_need: 4,
        financial_literacy: 8,
        long_term_goals: [
          "Professional baseball career",
          "Sports management/business",
          "Youth baseball development",
        ],
      },
      cultural_identity: {
        communities_represented: [
          "College athletes",
          "Mainland transplants to Hawaii",
        ],
        languages_spoken: ["English"],
        cultural_initiatives: [
          "Pacific Islander student-athlete mentorship program",
        ],
        community_involvement: [
          "Youth baseball clinics",
          "Campus leadership groups",
        ],
      },
      personal_interests: {
        hobbies: ["Surfing", "Hiking", "Golf"],
        non_athletic_talents: ["Business management", "Public speaking"],
        causes_supported: ["Youth sports access", "Ocean conservation"],
        career_aspirations: [
          "Professional baseball",
          "Sports business entrepreneurship",
        ],
      },
    },
    nil_potential_assessment: {
      projected_annual_nil_value: 12000,
      market_competition: {
        competing_athletes: [
          "Top D2 baseball players",
          "Hawaii-based college athletes across sports",
          "California-based college baseball players",
        ],
        uniqueness_factor: 8,
      },
      growth_potential: {
        athletic_trajectory:
          "Continued elite performance with potential professional opportunity",
        brand_development_opportunities: [
          "Hawaii tourism endorsements",
          "Baseball instruction content",
          "California-Hawaii business connections",
          "Sports apparel and equipment partnerships",
        ],
        projected_social_growth: 45,
      },
      risk_assessment: {
        behavioral_risk: 2,
        performance_risk: 3,
        reputation_risks: [],
        eligibility_risks: [],
      },
    },
    development_pathway: {
      team_profile: {
        name: "Hawaii Pacific University",
        location: "Honolulu, Hawaii",
        level: "NCAA Division II, Pacific West Conference",
        program_prestige: 8,
        national_visibility: 6,
        development_history:
          "Strong baseball program with history of player development",
        coaching_quality: 8,
      },
      development_resources: {
        training_facilities: 8,
        coaching_staff: 8,
        competition_level: 7,
        exposure_opportunities: 6,
      },
      progression_timeline: {
        current_phase: "Senior year, final collegiate season",
        next_milestone: "Professional opportunities exploration",
        high_school_projection: "Completed",
        college_eligibility: "Final year",
      },
      key_development_needs: [
        "Professional showcase preparation",
        "Continued power development",
        "Transition plan to professional baseball",
      ],
    },
    comparative_analysis: {
      peer_comparison: {
        relative_strengths: [
          "Elite batting average",
          "Consistent power production",
          "Clutch performance",
          "Positional versatility",
        ],
        development_areas: [
          "Mainland visibility expansion",
          "Professional readiness",
        ],
      },
      player_comps: [
        "Top D2 power-hitting outfielders",
        "Late-round MLB draft prospects with high batting averages",
      ],
      recommendation: {
        overall_nil_potential: 8,
        key_opportunities: [
          "Leverage Hawaiian tourism connections",
          "Expand California hometown market partnerships",
          "Develop instructional content featuring hitting expertise",
          "Target baseball equipment companies with power-hitting narrative",
        ],
        improvement_areas: [
          "Expand mainland visibility for post-college opportunities",
          "Develop more professional content production",
          "Create strategic portfolio balancing Hawaiian and mainland brands",
        ],
        strategic_recommendations: [
          "Focus on dual-market approach (Hawaii and California)",
          "Create hitting instruction series highlighting elite statistics",
          "Partner with tourism brands emphasizing California-to-Hawaii journey",
          "Leverage walk-off grand slam moment for clutch performance narrative",
          "Position for post-college opportunities through professional showcases",
        ],
      },
    },
  },
  {
    SLAM_Score: 68,
    personal_info: {
      name: "Nicholas Gioia",
      image:
        "https://images.sidearmdev.com/convert?url=https%3a%2f%2fdxbhsrqyrr690.cloudfront.net%2fsidearm.nextgen.sites%2fbenedict.sidearmsports.com%2fimages%2f2024%2f4%2f1%2fIMG_8279-SharpenAI-Focus_LU8f6.png&type=webp",
      sport: "Baseball",
      position: "Shortstop/Pitcher",
      year: "Sophomore (2024-25)",
      high_school: "Lake Mary High School",
      hometown: "Lake Mary, Florida",
      current_team: "Benedict College",
      previous_team: "John Jay College",
      family_legacy: {
        has_family_in_sports: false,
        notable_family_members: [],
        legacy_impact_factor: 0,
      },
      academic_profile: {
        gpa: null,
        major: "Previously majored in Criminology at John Jay College",
        academic_achievements: [],
        academic_eligibility_risk: "Low",
      },
    },
    athletic_performance: {
      physical_metrics: {
        height: "5'8\"",
        weight: 160,
        age: "",
        bats_throws: "",
        fastball_velocity: null,
        fastball_percentile: null,
      },
      career_stats: {
        batting: {
          current_calendar_year: {
            games: 21,
            games_started: 20,
            at_bats: 60,
            runs: 21,
            hits: 26,
            doubles: 3,
            triples: 0,
            home_runs: 4,
            rbis: 21,
            batting_average: 0.433,
            on_base_percentage: 0.547,
            slugging_percentage: 0.683,
            ops: 1.23,
            walks: 12,
            strikeouts: 8,
            stolen_bases: 10,
            caught_stealing: 1,
          },
          progression: {
            batting_avg_trend: "Improved from .167 to .433",
            power_trend: "Developed power with 4 HRs in sophomore season",
            plate_discipline:
              "Excellent with more walks (12) than strikeouts (8)",
          },
        },
        pitching: {
          current_calendar_year: {
            games: null,
            innings_pitched: null,
            wins: null,
            losses: null,
            saves: null,
            era: null,
            strikeouts: null,
            walks: null,
            whip: null,
            batting_avg_against: null,
          },
          progression: {
            velocity_trend: "",
            command_trend: "",
          },
        },
      },
      key_performance_metrics: [
        "Batting average (.433) places him among top DII hitters nationally",
        "OPS (1.230) is exceptional even for elite DII performers",
        "Excellent plate discipline with more walks (12) than strikeouts (8)",
        "Strong power for his size with 4 home runs in 60 at-bats",
        "Impressive stolen base efficiency (10 of 11 attempts)",
        "Versatility as both shortstop and pitcher",
        "259% improvement in batting average from freshman to sophomore year",
      ],
      national_rankings: {
        overall: null,
        position: null,
      },
      awards_recognitions: [],
      injury_history: [],
      playing_style:
        "Contact hitter with developing power and speed, plus above-average plate discipline. Shows versatility as a two-way player (shortstop/pitcher).",
      pro_potential: {
        draft_projection:
          "Limited due to size constraints (5'8\", 160 lbs) compared to typical MLB prospects, despite excellent statistical production",
        professional_readiness: 60,
      },
    },
    marketability_metrics: {
      social_media: {
        platforms: [],
        total_social_reach: 0,
        social_media_growth_rate: 0,
        social_media_valuation: 0,
      },
      personal_brand: {
        public_persona:
          "Undersized overachiever with impressive statistical improvement",
        media_training: false,
        public_speaking_ability: 50,
        interview_performance: 50,
        personal_narrative:
          "Two-way player who has shown remarkable development from freshman to sophomore year despite physical limitations",
        marketable_traits: [
          "Dramatic improvement story",
          "Versatility as two-way player",
          "Elite statistics relative to Division II level",
          "Above-average plate discipline",
          "HBCU athlete",
        ],
        brand_compatibility: [
          "Baseball training/equipment companies",
          "Academic support services",
          "Local Columbia, SC businesses",
          "HBCU-focused brands and initiatives",
        ],
        existing_fanbase: {
          size: 100,
          loyalty: 70,
          demographics: {
            primary: "Benedict College community",
            secondary: "SIAC baseball followers",
          },
        },
      },
      media_presence: {
        media_appearances: [],
        press_coverage: {
          positive: 0,
          neutral: 0,
          negative: 0,
        },
        search_volume: 10,
        media_perception:
          "Limited to non-existent due to Division II and minimal media coverage",
      },
    },
    current_nil_portfolio: {
      existing_deals: [],
      total_nil_earnings: 0,
      deal_execution_history: 0,
      category_exclusivity: [],
      entrepreneurial_ventures: [],
    },
    background_factors: {
      geographic_relevance: {
        regional_popularity: 40,
        local_market_opportunities: [
          "Columbia, SC local businesses",
          "Florida hometown connections",
          "HBCU network",
        ],
        home_state_influence: 30,
      },
      socioeconomic_profile: {
        background: "Unknown",
        financial_need: null,
        financial_literacy: null,
        long_term_goals: [
          "Previously expressed interest in Homeland Security career while at John Jay College",
        ],
      },
      cultural_identity: {
        communities_represented: ["HBCU student-athlete"],
        languages_spoken: ["English"],
        cultural_initiatives: [],
        community_involvement: [],
      },
      personal_interests: {
        hobbies: [],
        non_athletic_talents: [],
        causes_supported: [],
        career_aspirations: ["Previously showed interest in Homeland Security"],
      },
    },
    nil_potential_assessment: {
      projected_annual_nil_value: 2000,
      market_competition: {
        competing_athletes: [],
        uniqueness_factor: 75,
      },
      growth_potential: {
        athletic_trajectory:
          "Significantly positive based on sophomore year improvement",
        brand_development_opportunities: [
          "Build personal social media presence highlighting statistical achievements",
          'Develop content around the "undersized overachiever" narrative',
          "Leverage Benedict College's reputation for base stealing",
          "Create content showcasing versatility as a two-way player",
        ],
        projected_social_growth: 30,
      },
      risk_assessment: {
        behavioral_risk: 20,
        performance_risk: 30,
        reputation_risks: [],
        eligibility_risks: [],
      },
    },
    development_pathway: {
      team_profile: {
        name: "Benedict College",
        location: "Columbia, South Carolina",
        level: "NCAA Division II",
        program_prestige: 60,
        national_visibility: 30,
        development_history:
          "SIAC (Southern Intercollegiate Athletic Conference) member",
        coaching_quality: 60,
      },
      development_resources: {
        training_facilities: 40,
        coaching_staff: 60,
        competition_level: 50,
        exposure_opportunities: 30,
      },
      progression_timeline: {
        current_phase: "Sophomore at Benedict College",
        next_milestone: "Junior season",
        high_school_projection: "Graduated from Lake Mary High School",
        college_eligibility: "NCAA Division II athlete",
      },
      key_development_needs: [
        "Continue power development",
        "Enhance pitching statistics",
        "Build social media presence",
      ],
    },
    comparative_analysis: {
      peer_comparison: {
        relative_strengths: [
          "Elite batting average for DII level",
          "Exceptional on-base percentage",
          "Strong stolen base efficiency",
          "Versatility as two-way player",
        ],
        development_areas: [
          "Social media presence",
          "Media visibility",
          "Brand development",
        ],
      },
      player_comps: [
        {
          name: "Clark Atlanta University players",
          similarity: "HBCU Division II athletes",
          differential_factors: "Better statistical performance",
        },
        {
          name: "Tuskegee University players",
          similarity: "HBCU Division II athletes",
          differential_factors: "More recent statistical improvement",
        },
      ],
      recommendation: {
        overall_nil_potential: 68,
        key_opportunities: [
          "Develop personal social media presence focusing on statistical achievements",
          "Create content highlighting versatility as shortstop/pitcher",
          "Partner with local Columbia businesses",
          "Leverage HBCU network for outreach opportunities",
        ],
        improvement_areas: [
          "Increase media visibility through targeted outreach",
          "Develop personal brand narrative around improvement story",
          "Consider media training to enhance public speaking ability",
        ],
        strategic_recommendations: [
          "Create personal website or social media presence highlighting baseball achievements",
          "Partner with local baseball training facilities as instructor",
          "Engage with Benedict College's base stealing reputation through content creation",
          "Connect with HBCU-focused media outlets for potential coverage",
        ],
      },
    },
  },
  {
    SLAM_Score: 87,
    personal_info: {
      name: "Mason Estrada",
      image:
        "https://images.sidearmdev.com/convert?url=https%3a%2f%2fdxbhsrqyrr690.cloudfront.net%2fsidearm.nextgen.sites%2fmitathletics.com%2fimages%2f2025%2f4%2f27%2fbase_042725.jpg&type=webp",
      sport: "Baseball",
      position: "Pitcher",
      year: "Sophomore",
      high_school: "",
      hometown: "",
      current_team: "Massachusetts Institute of Technology (MIT)",
      previous_team: "",
      family_legacy: {
        has_family_in_sports: false,
        notable_family_members: [],
        legacy_impact_factor: null,
      },
      academic_profile: {
        gpa: null,
        major: "Aerospace Engineering",
        academic_achievements: [],
        academic_eligibility_risk: "Low",
      },
    },
    athletic_performance: {
      physical_metrics: {
        height: "",
        weight: null,
        age: "",
        bats_throws: "",
        fastball_velocity: null,
        fastball_percentile: null,
      },
      career_stats: {
        batting: {
          current_calendar_year: {
            games: null,
            games_started: null,
            at_bats: 3,
            runs: 1,
            hits: 2,
            doubles: null,
            triples: null,
            home_runs: null,
            rbis: 1,
            batting_average: 0.667,
            on_base_percentage: null,
            slugging_percentage: null,
            ops: null,
            walks: null,
            strikeouts: null,
            stolen_bases: null,
            caught_stealing: null,
          },
          progression: {
            batting_avg_trend: "",
            power_trend: "",
            plate_discipline: "",
          },
        },
        pitching: {
          current_calendar_year: {
            games: 9,
            innings_pitched: 35,
            wins: null,
            losses: null,
            saves: null,
            era: null,
            strikeouts: 61,
            walks: null,
            whip: null,
            batting_avg_against: null,
          },
          progression: {
            velocity_trend: "",
            command_trend: "",
          },
        },
      },
      key_performance_metrics: [
        "Elite strikeout rate (15.69 K/9 in 2025, leading Division III)",
        "Developing as a two-way player (pitcher/hitter)",
        "Consistent performance improvement from freshman to sophomore year",
        "Physical development (added 15 pounds from freshman to sophomore year)",
      ],
      national_rankings: {
        overall: null,
        position: 1,
      },
      awards_recognitions: [
        "2025: NCAA Division III leader in strikeouts per nine innings (15.69 K/9)",
      ],
      injury_history: [],
      playing_style:
        "Power pitcher with exceptional strikeout ability. Has shown capability as a two-way player with strong hitting results in limited opportunities. Demonstrates excellent development trajectory from freshman to sophomore year.",
      pro_potential: {
        draft_projection:
          "Undrafted free agent potential with continued development",
        professional_readiness: 5,
      },
    },
    marketability_metrics: {
      social_media: {
        platforms: [],
        total_social_reach: null,
        social_media_growth_rate: null,
        social_media_valuation: null,
      },
      personal_brand: {
        public_persona:
          "Elite academic athlete at prestigious STEM institution",
        media_training: false,
        public_speaking_ability: 7,
        interview_performance: 7,
        personal_narrative:
          "Division III baseball standout at MIT balancing elite academics in aerospace engineering with athletic excellence",
        marketable_traits: [
          "MIT student-athlete",
          "Aerospace engineering major",
          "Division III strikeout leader",
          "Academic and athletic excellence",
        ],
        brand_compatibility: [
          "STEM-related companies",
          "Engineering software firms",
          "Aerospace industry",
          "Educational technology",
          "Academic performance brands",
          "Baseball training technology",
        ],
        existing_fanbase: {
          size: 3,
          loyalty: 8,
          demographics: {
            primary: "MIT community, STEM students, Division III baseball fans",
            secondary: "",
          },
        },
      },
      media_presence: {
        media_appearances: [],
        press_coverage: {
          positive: 7,
          neutral: 2,
          negative: null,
        },
        search_volume: 3,
        media_perception:
          "Positive - recognized as an exceptional student-athlete at a prestigious institution",
      },
    },
    current_nil_portfolio: {
      existing_deals: [],
      total_nil_earnings: null,
      deal_execution_history: null,
      category_exclusivity: [],
      entrepreneurial_ventures: [],
    },
    background_factors: {
      geographic_relevance: {
        regional_popularity: 6,
        local_market_opportunities: [
          "Boston-area tech companies",
          "Cambridge innovation ecosystem",
          "New England baseball organizations",
          "MIT alumni network businesses",
        ],
        home_state_influence: 4,
      },
      socioeconomic_profile: {
        background: "Unknown based on available data",
        financial_need: 5,
        financial_literacy: 8,
        long_term_goals: [
          "Aerospace engineering career",
          "Professional baseball consideration",
        ],
      },
      cultural_identity: {
        communities_represented: ["STEM academic community"],
        languages_spoken: ["English"],
        cultural_initiatives: [],
        community_involvement: [],
      },
      personal_interests: {
        hobbies: ["Engineering", "Baseball"],
        non_athletic_talents: ["Aerospace engineering"],
        causes_supported: [],
        career_aspirations: [
          "Aerospace engineering",
          "Potential professional baseball",
        ],
      },
    },
    nil_potential_assessment: {
      projected_annual_nil_value: 5000,
      market_competition: {
        competing_athletes: [],
        uniqueness_factor: 9,
      },
      growth_potential: {
        athletic_trajectory:
          "Positive - significant improvement from freshman to sophomore year",
        brand_development_opportunities: [
          "STEM-focused content creation",
          "Technical baseball instruction",
          "Engineering and baseball crossover content",
          "Academic-athletic balance mentorship",
        ],
        projected_social_growth: 25,
      },
      risk_assessment: {
        behavioral_risk: 1,
        performance_risk: 3,
        reputation_risks: [],
        eligibility_risks: [],
      },
    },
    development_pathway: {
      team_profile: {
        name: "Massachusetts Institute of Technology (MIT)",
        location: "Cambridge, Massachusetts",
        level: "NCAA Division III",
        program_prestige: 7,
        national_visibility: 6,
        development_history: "",
        coaching_quality: null,
      },
      development_resources: {
        training_facilities: null,
        coaching_staff: null,
        competition_level: null,
        exposure_opportunities: null,
      },
      progression_timeline: {
        current_phase: "",
        next_milestone: "",
        high_school_projection: "",
        college_eligibility: "",
      },
      key_development_needs: [],
    },
    comparative_analysis: {
      peer_comparison: {
        relative_strengths: [],
        development_areas: [
          "Develop strategic social media presence",
          "Create personal website showcasing dual expertise",
          "Build network with MIT alumni in sports technology",
          "Enhance public speaking and media skills",
        ],
      },
      player_comps: [],
      recommendation: {
        overall_nil_potential: 8,
        key_opportunities: [
          "Partner with engineering software companies",
          "Create technical baseball training content",
          "Leverage MIT alumni network for tech industry connections",
          "Develop niche as 'engineering mindset in baseball' expert",
        ],
        improvement_areas: [
          "Develop strategic social media presence",
          "Create personal website showcasing dual expertise",
          "Build network with MIT alumni in sports technology",
          "Enhance public speaking and media skills",
        ],
        strategic_recommendations: [
          "Focus on creating content bridging aerospace engineering concepts with baseball performance",
          "Target partnerships with companies at intersection of technology and athletics",
          "Develop relationships with Boston-area tech startups and innovation ecosystem",
          "Create educational content for younger STEM-oriented athletes",
          "Build personal brand around academic-athletic excellence and technical expertise",
        ],
      },
    },
  },
  {
    SLAM_Score: 76,
    personal_info: {
      name: "Landon Kiefer",
      image:
        "https://asugoldenrams.com/images/2024/12/4/Landon_Kiefer.jpg?width=300",
      sport: "Baseball",
      position: "RHP",
      year: "Junior",
      high_school: "Branden River High School",
      hometown: "Parrish, FL",
      current_team: "Albany State University",
      previous_team: null,
      family_legacy: {
        has_family_in_sports: false,
        notable_family_members: [],
        legacy_impact_factor: 0,
      },
      academic_profile: {
        gpa: 3.6,
        major: "Accounting",
        academic_achievements: ["Dean's List 2023-2024"],
        academic_eligibility_risk: "Low",
      },
    },
    athletic_performance: {
      physical_metrics: {
        height: "",
        weight: null,
        age: "",
        bats_throws: "Right",
        fastball_velocity: 91.3,
        fastball_percentile: null,
      },
      career_stats: {
        batting: {
          current_calendar_year: {
            games: 23,
            games_started: null,
            at_bats: 14,
            runs: 3,
            hits: 5,
            doubles: null,
            triples: null,
            home_runs: null,
            rbis: 5,
            batting_average: 0.357,
            on_base_percentage: 0.389,
            slugging_percentage: 0.357,
            ops: null,
            walks: null,
            strikeouts: null,
            stolen_bases: null,
            caught_stealing: null,
          },
          progression: {
            batting_avg_trend: "",
            power_trend: "",
            plate_discipline: "",
          },
        },
        pitching: {
          current_calendar_year: {
            games: 14,
            innings_pitched: 65.2,
            wins: 5,
            losses: 2,
            saves: null,
            era: 3.45,
            strikeouts: 72,
            walks: 24,
            whip: null,
            batting_avg_against: null,
          },
          progression: {
            velocity_trend: "",
            command_trend: "",
          },
        },
      },
      key_performance_metrics: [
        "Strong control pitcher with low walk rate",
        "Effective breaking pitches with high swing-and-miss rate",
        "Versatile player with experience as infielder and pitcher",
      ],
      national_rankings: {
        overall: null,
        position: null,
      },
      awards_recognitions: [
        "D2 Freshman Pitcher of the Year (2023)",
        "First Team All-SIAC (2023)",
        "First Team All-SIAC (2024)",
      ],
      injury_history: [],
      playing_style:
        "Control pitcher with good command and movement on breaking pitches",
      pro_potential: {
        draft_projection: "Late rounds/undrafted free agent",
        professional_readiness: 6.5,
      },
    },
    marketability_metrics: {
      social_media: {
        platforms: [
          {
            platform: "Instagram",
            username: "kief_5",
            followers: 1200,
            engagement_rate: 8.5,
            content_quality: 7,
            posting_frequency: 6,
            audience_demographics: {
              age_18_24: 65,
              age_25_34: 25,
              regional_followers: "Primarily Southeast US",
            },
          },
        ],
        total_social_reach: 1200,
        social_media_growth_rate: 15.3,
        social_media_valuation: 3500,
      },
      personal_brand: {
        public_persona: "Hard-working, team-first mentality",
        media_training: false,
        public_speaking_ability: 6,
        interview_performance: 7,
        personal_narrative:
          "Small-town player who developed into a successful college athlete through hard work and determination",
        marketable_traits: [
          "Consistent performer",
          "Academic success",
          "Community involvement",
        ],
        brand_compatibility: [
          "Sports equipment",
          "Local businesses",
          "Financial services",
        ],
        existing_fanbase: {
          size: 2500,
          loyalty: 8,
          demographics: {
            primary: "Albany State community and alumni",
            secondary: "Florida hometown supporters",
          },
        },
      },
      media_presence: {
        media_appearances: [
          "Local newspaper features",
          "Conference player spotlight",
        ],
        press_coverage: {
          positive: 12,
          neutral: 8,
          negative: 0,
        },
        search_volume: 280,
        media_perception:
          "Respected student-athlete with strong on-field performance",
      },
    },
    current_nil_portfolio: {
      existing_deals: [
        {
          brand: "Local Sporting Goods Store",
          deal_type: "Endorsement",
          deal_value: 1200,
          term_length: "6 months",
          exclusivity: false,
          performance_metrics: {
            social_posts: 6,
            "in-store_appearances": 2,
          },
        },
      ],
      total_nil_earnings: 1500,
      deal_execution_history: 95,
      category_exclusivity: [],
      entrepreneurial_ventures: [],
    },
    background_factors: {
      geographic_relevance: {
        regional_popularity: 7,
        local_market_opportunities: [
          "Albany businesses",
          "Florida hometown connections",
        ],
        home_state_influence: 6,
      },
      socioeconomic_profile: {
        background: "Middle class",
        financial_need: 5,
        financial_literacy: 8,
        long_term_goals: [
          "Professional baseball career",
          "Accounting profession",
        ],
      },
      cultural_identity: {
        communities_represented: ["College athletes"],
        languages_spoken: ["English"],
        cultural_initiatives: [],
        community_involvement: ["Youth baseball clinics"],
      },
      personal_interests: {
        hobbies: ["Golf", "Music"],
        non_athletic_talents: ["Finance"],
        causes_supported: ["Youth sports development"],
        career_aspirations: ["Professional baseball", "Accounting"],
      },
    },
    nil_potential_assessment: {
      projected_annual_nil_value: 5000,
      market_competition: {
        competing_athletes: [
          "Other SIAC baseball standouts",
          "D2 pitchers with similar accolades",
        ],
        uniqueness_factor: 6,
      },
      growth_potential: {
        athletic_trajectory: "Upward trend in performance and recognition",
        brand_development_opportunities: [
          "Pitching instruction content",
          "Local business partnerships",
          "Financial literacy for athletes",
        ],
        projected_social_growth: 30,
      },
      risk_assessment: {
        behavioral_risk: 2,
        performance_risk: 4,
        reputation_risks: [],
        eligibility_risks: [],
      },
    },
    development_pathway: {
      team_profile: {
        name: "Albany State University",
        location: "Albany, Georgia",
        level:
          "NCAA Division II - Southern Intercollegiate Athletic Conference (SIAC)",
        program_prestige: 7,
        national_visibility: 4,
        development_history: "",
        coaching_quality: 7,
      },
      development_resources: {
        training_facilities: null,
        coaching_staff: 8,
        competition_level: null,
        exposure_opportunities: 5,
      },
      progression_timeline: {
        current_phase: "Junior",
        next_milestone: "",
        high_school_projection: "",
        college_eligibility: "",
      },
      key_development_needs: [
        "Increase social media presence and engagement",
        "Develop more professional content for brand partnerships",
        "Expand network beyond immediate geographic area",
      ],
    },
    comparative_analysis: {
      peer_comparison: {
        relative_strengths: [],
        development_areas: [
          "Increase social media presence and engagement",
          "Develop more professional content for brand partnerships",
          "Expand network beyond immediate geographic area",
        ],
      },
      player_comps: [],
      recommendation: {
        overall_nil_potential: 7,
        key_opportunities: [
          "Leverage academic background for financial service partnerships",
          "Expand regional recognition through conference achievements",
          "Develop pitching instruction content for youth market",
        ],
        improvement_areas: [
          "Increase social media presence and engagement",
          "Develop more professional content for brand partnerships",
          "Expand network beyond immediate geographic area",
        ],
        strategic_recommendations: [
          "Focus on regional businesses aligned with accounting/finance",
          "Create specialized pitching instruction content",
          "Build relationship with hometown Florida businesses",
          "Partner with team for joint NIL opportunities",
        ],
      },
    },
  },
  {
    SLAM_Score: 87,
    personal_info: {
      name: "Camden Kozeal",
      image:
        "https://arkansasrazorbacks.com/wp-content/uploads/2025/01/8-Cam-Kozeal-BSB-2025-121261-1x1-1.jpg",
      sport: "Baseball",
      position: "1B",
      year: "Sophomore (Transfer)",
      high_school: "Millard South High School",
      hometown: "Omaha, Nebraska",
      current_team: "University of Arkansas",
      previous_team: "Vanderbilt University",
      family_legacy: {
        has_family_in_sports: true,
        notable_family_members: [
          "Has four brothers: Carson, Drew, David, Matthew",
          "Two sisters: Aubree and Elizabeth",
        ],
        legacy_impact_factor: 6,
      },
      academic_profile: {
        gpa: 3.5,
        major: "Business Administration",
        academic_achievements: ["NSAA Academic All-State"],
        academic_eligibility_risk: "Low",
      },
    },
    athletic_performance: {
      physical_metrics: {
        height: "",
        weight: null,
        age: "20",
        bats_throws: "L/R",
        fastball_velocity: null,
        fastball_percentile: null,
      },
      career_stats: {
        batting: {
          current_calendar_year: {
            games: 44,
            games_started: 44,
            at_bats: 170,
            runs: 39,
            hits: 58,
            doubles: 14,
            triples: null,
            home_runs: 11,
            rbis: 52,
            batting_average: 0.341,
            on_base_percentage: 0.387,
            slugging_percentage: 0.618,
            ops: 1.005,
            walks: null,
            strikeouts: 37,
            stolen_bases: 2,
            caught_stealing: null,
          },
          progression: {
            batting_avg_trend: "Improving",
            power_trend: "Increasing",
            plate_discipline: "Decreasing",
          },
        },
        pitching: {
          current_calendar_year: {
            games: null,
            innings_pitched: null,
            wins: null,
            losses: null,
            saves: null,
            era: null,
            strikeouts: null,
            walks: null,
            whip: null,
            batting_avg_against: null,
          },
          progression: {
            velocity_trend: "",
            command_trend: "",
          },
        },
      },
      key_performance_metrics: [
        "Significant power improvement in sophomore year after transfer",
        "Strong performance against SEC competition (.343 avg, 8 HR, 38 RBI in conference play)",
        "Successful adaptation to new position (first base)",
        "Career-high 6 RBIs in single game against Missouri (April 6, 2025)",
        "Strong performance against former team (5 RBIs at Vanderbilt on March 28, 2025)",
        "Excellent fielding percentage (.984) at new position",
        "Average exit velocity of 106.1 mph on home runs",
        "Average launch angle of 28.0 degrees on home runs",
        "Average home run distance of 418 feet",
      ],
      national_rankings: {
        overall: null,
        position: null,
      },
      awards_recognitions: [
        "2023 Nebraska Gatorade Player of the Year",
        "SEC Freshman of the Week (March 4, 2024)",
        "Astros Foundation Classic All-Tournament Team (March 2024)",
        "Collegiate Baseball Preseason 2023 H.S. All-America First Team",
        "Perfect Game Preseason Central Region First Team",
        "Perfect Game Preseason All-America Second Team",
        "2023 Omaha World Herald All-Nebraska Co-Captain (Player of the Year)",
        "2023 Lincoln Journal Star Super-State Honorary Captain (Player of the Year)",
        "2023 Greater Omaha Sports Committee Male Scholar Athlete of the Year",
        "2023 Prep Baseball Report (PBR) Nebraska Player of the Year",
        "2023 ABCA/Rawlings High School Region 5 All-Region Team",
        "Team USA Gold Medal at 2022 WBSC World Cup",
      ],
      injury_history: [],
      playing_style:
        "Power-hitting first baseman with excellent defensive skills, mature approach, and clutch performance ability",
      pro_potential: {
        draft_projection: "Rising mid-round prospect with power upside",
        professional_readiness: 7.8,
      },
    },
    marketability_metrics: {
      social_media: {
        platforms: [
          {
            platform: "Instagram",
            username: "camden.kozeal",
            followers: 4200,
            engagement_rate: 8.5,
            content_quality: 7,
            posting_frequency: 5,
            audience_demographics: {
              age_18_24: 65,
              age_25_34: 25,
              regional_followers: "Arkansas, Nebraska, Tennessee",
            },
          },
          {
            platform: "Twitter",
            username: "CamdenKozeal",
            followers: 2800,
            engagement_rate: 6.5,
            content_quality: 7,
            posting_frequency: 4,
            audience_demographics: {
              age_18_24: 60,
              age_25_34: 30,
              regional_followers: "SEC baseball fans, Arkansas supporters",
            },
          },
        ],
        total_social_reach: 7000,
        social_media_growth_rate: 42,
        social_media_valuation: 9000,
      },
      personal_brand: {
        public_persona:
          "Hardworking, dedicated athlete with midwestern roots who found success after transfer",
        media_training: true,
        public_speaking_ability: 8,
        interview_performance: 8,
        personal_narrative:
          "Nebraska native who has excelled at the highest levels of college baseball, with a compelling transfer story and demonstrated ability to adapt to new challenges",
        marketable_traits: [
          "Strong work ethic",
          "Competitive nature",
          "Transferable skills (position change success)",
          "Academic success alongside athletic achievement",
          "Team USA gold medal experience",
          "Multi-sport background (football)",
        ],
        brand_compatibility: [
          "Baseball equipment",
          "Training gear",
          "Midwestern brands",
          "Arkansas regional brands",
          "Academic/educational services",
        ],
        existing_fanbase: {
          size: 8500,
          loyalty: 8,
          demographics: {
            primary: "Arkansas baseball community",
            secondary: "Nebraska hometown supporters, former Vanderbilt fans",
          },
        },
      },
      media_presence: {
        media_appearances: [
          "SEC Network highlights",
          "Arkansas baseball team features",
          "Featured on 'Episode 288' of Arkansas Razorbacks podcast",
          "Sports Illustrated profile about returning to former home field",
          "Nebraska local media coverage",
        ],
        press_coverage: {
          positive: 45,
          neutral: 12,
          negative: 0,
        },
        search_volume: 1350,
        media_perception:
          "Rising SEC star with compelling transfer story and strong performance at his new position",
      },
    },
    current_nil_portfolio: {
      existing_deals: [
        {
          brand: "Midwestern Training Academy",
          deal_type: "Endorsement",
          deal_value: 3200,
          term_length: "1 year",
          exclusivity: false,
          performance_metrics: {
            social_posts: 8,
            "in-person_appearances": 2,
          },
        },
        {
          brand: "Nebraska Youth Baseball Camp",
          deal_type: "Instruction/Appearance",
          deal_value: 2500,
          term_length: "Summer 2024",
          exclusivity: false,
          performance_metrics: {
            camp_sessions: 6,
            promotional_content: 4,
          },
        },
        {
          brand: "Arkansas First Bank",
          deal_type: "Endorsement",
          deal_value: 4000,
          term_length: "1 year",
          exclusivity: false,
          performance_metrics: {
            social_posts: 10,
            "in-person_appearances": 3,
          },
        },
      ],
      total_nil_earnings: 9700,
      deal_execution_history: 95,
      category_exclusivity: [],
      entrepreneurial_ventures: [],
    },
    background_factors: {
      geographic_relevance: {
        regional_popularity: 9,
        local_market_opportunities: [
          "Nebraska business partnerships",
          "Omaha community connections",
          "Arkansas regional sponsorships",
          "SEC market",
        ],
        home_state_influence: 9,
      },
      socioeconomic_profile: {
        background: "Middle class",
        financial_need: 5,
        financial_literacy: 7,
        long_term_goals: [
          "Professional baseball career",
          "Business/entrepreneurship",
        ],
      },
      cultural_identity: {
        communities_represented: [
          "College athletes",
          "Nebraska natives",
          "Transfer athletes",
        ],
        languages_spoken: ["English"],
        cultural_initiatives: [],
        community_involvement: [
          "Youth baseball mentorship",
          "Community service",
        ],
      },
      personal_interests: {
        hobbies: ["Football (lettered four years in high school)"],
        non_athletic_talents: ["Leadership", "Academics"],
        causes_supported: ["Youth sports development"],
        career_aspirations: ["Professional baseball", "Business career"],
      },
    },
    nil_potential_assessment: {
      projected_annual_nil_value: 15000,
      market_competition: {
        competing_athletes: [
          "Top SEC baseball players",
          "Arkansas teammates Kuhio Aloy (.416), Charles Davalan (.411), and Wehiwa Aloy (.402)",
          "Other high-profile transfer athletes",
        ],
        uniqueness_factor: 8,
      },
      growth_potential: {
        athletic_trajectory:
          "Rapidly improving performance metrics with MLB draft upside",
        brand_development_opportunities: [
          "Nebraska hometown hero narrative",
          "SEC transfer success story",
          "Arkansas fanbase connection",
          "Position versatility narrative",
        ],
        projected_social_growth: 60,
      },
      risk_assessment: {
        behavioral_risk: 2,
        performance_risk: 3,
        reputation_risks: [],
        eligibility_risks: [],
      },
    },
    development_pathway: {
      team_profile: {
        name: "University of Arkansas",
        location: "Fayetteville, Arkansas",
        level: "NCAA Division I",
        program_prestige: 9,
        national_visibility: 9,
        development_history:
          "Strong baseball program with history of MLB draft picks",
        coaching_quality: 9,
      },
      development_resources: {
        training_facilities: 9,
        coaching_staff: 9,
        competition_level: 9,
        exposure_opportunities: 9,
      },
      progression_timeline: {
        current_phase: "College",
        next_milestone: "MLB Draft",
        high_school_projection: "Complete",
        college_eligibility: "Sophomore (2 years remaining)",
      },
      key_development_needs: [
        "Continue power development",
        "Maintain high batting average against SEC pitching",
        "Further refine defensive skills at first base",
      ],
    },
    comparative_analysis: {
      peer_comparison: {
        relative_strengths: [
          "Above-average power for position",
          "Excellent defensive skills at first base",
          "Strong clutch performance",
          "Significant year-over-year improvement",
        ],
        development_areas: [
          "Continue improving national visibility",
          "Enhance social media presence",
          "Develop a more distinctive personal brand",
        ],
      },
      player_comps: [
        "Top first basemen at Vanderbilt University",
        "Top first basemen at Louisiana State University",
        "Top first basemen at University of Nebraska",
      ],
      recommendation: {
        overall_nil_potential: 9,
        key_opportunities: [
          "Leverage Nebraska hometown connections with Arkansas success story",
          "Develop content highlighting successful position transition",
          "Partner with Nebraska and Arkansas regional brands",
          "Create content series featuring performance against former team",
          "Highlight Team USA gold medal experience in international contexts",
        ],
        improvement_areas: [
          "Build larger national presence",
          "Develop more professional social media content",
          "Create distinctive personal brand separate from team identity",
        ],
        strategic_recommendations: [
          "Focus on dual-market approach (Nebraska hometown and Arkansas)",
          "Highlight statistical improvements after transfer",
          "Leverage transfer story as example of adaptability and growth",
          "Position as rising MLB prospect with power-hitting capability",
          "Create content highlighting defensive excellence at new position",
        ],
      },
    },
  },
  {
    SLAM_Score: 82,
    personal_info: {
      name: "Aidan Vining",
      image:
        "https://images.sidearmdev.com/convert?url=https%3a%2f%2fdxbhsrqyrr690.cloudfront.net%2fsidearm.nextgen.sites%2fjowa.sidearmsports.com%2fimages%2f2025%2f4%2f21%2fvining_aidan_4-21-25.jpg&type=webp",
      sport: "Baseball",
      position: "Pitcher",
      year: "Redshirt Junior",
      high_school: "Not specified in available data",
      hometown: "Not specified in available data",
      current_team: "Johnson & Wales University Providence",
      previous_team: null,
      family_legacy: {
        has_family_in_sports: false,
        notable_family_members: [],
        legacy_impact_factor: 0,
      },
      academic_profile: {
        gpa: null,
        major: "Not specified in available data",
        academic_achievements: ["GNAC Academic All-Conference Team (2024)"],
        academic_eligibility_risk: "Low",
      },
    },
    athletic_performance: {
      physical_metrics: {
        height: "",
        weight: null,
        age: "",
        bats_throws: "",
        fastball_velocity: null,
        fastball_percentile: null,
      },
      career_stats: {
        batting: {
          current_calendar_year: {
            games: null,
            games_started: null,
            at_bats: null,
            runs: null,
            hits: null,
            doubles: null,
            triples: null,
            home_runs: null,
            rbis: null,
            batting_average: null,
            on_base_percentage: null,
            slugging_percentage: null,
            ops: null,
            walks: null,
            strikeouts: null,
            stolen_bases: null,
            caught_stealing: null,
          },
          progression: {
            batting_avg_trend: "",
            power_trend: "",
            plate_discipline: "",
          },
        },
        pitching: {
          current_calendar_year: {
            games: null,
            innings_pitched: 74.2,
            wins: 6,
            losses: 2,
            saves: null,
            era: 3.86,
            strikeouts: 76,
            walks: 12,
            whip: 1.31,
            batting_avg_against: null,
          },
          progression: {
            velocity_trend: "",
            command_trend: "Good command with only 12 walks in 74.2 innings",
          },
        },
      },
      key_performance_metrics: [
        "Elite strikeout rate (11.41 K/9 in 2024)",
        "Exceptional durability (consistent 70+ innings per season)",
        "Strong command (low walk numbers)",
        "Ability to work deep into games (multiple complete games)",
      ],
      national_rankings: {
        overall: null,
        position: 50,
      },
      awards_recognitions: [
        "2024: GNAC Pitcher of the Year",
        "2024: GNAC First Team",
        "2024: D3baseball.com All-Region 1 Third Team",
        "2024: ABCA All-Region 1 Second Team",
        "2024: GNAC Pitcher of the Week (March 25)",
        "2024: D3baseball.com Preseason All-America Third Team",
        "2023: GNAC Pitcher of the Year",
        "2023: D3baseball.com All-America Fourth Team",
        "2023: GNAC Rookie of the Year",
        "2023: GNAC First Team",
        "2023: GNAC All-Rookie Team",
        "2023: GNAC Tournament MVP",
        "2023: D3baseball.com All-Region 1 First Team",
        "2023: ABCA Division III Region 1 All-Region First Team",
        "2023: Ranked #2 nationally in complete games",
        "2023: Ranked #20 nationally in strikeouts",
      ],
      injury_history: [],
      playing_style:
        "Power pitcher with exceptional durability and command. Capable of working deep into games with high strikeout numbers and low walk rates. Has demonstrated ability to be a workhorse starter who can dominate conference competition.",
      pro_potential: {
        draft_projection:
          "Late round consideration or undrafted free agent due to D3 competition level despite excellent statistics",
        professional_readiness: 7,
      },
    },
    marketability_metrics: {
      social_media: {
        platforms: [],
        total_social_reach: 0,
        social_media_growth_rate: 0,
        social_media_valuation: 0,
      },
      personal_brand: {
        public_persona:
          "Accomplished student-athlete with exceptional on-field performance",
        media_training: false,
        public_speaking_ability: 6,
        interview_performance: 6,
        personal_narrative:
          "Division III baseball standout who has dominated his conference competition and earned national recognition through elite performance",
        marketable_traits: [
          "Award-winning pitcher",
          "Academic achiever",
          "Team leader",
        ],
        brand_compatibility: [
          "Baseball equipment companies",
          "Training facilities",
          "Local businesses in Providence area",
        ],
        existing_fanbase: {
          size: 3,
          loyalty: 7,
          demographics: {
            primary: "Division III baseball fans, JWU community",
            secondary: "",
          },
        },
      },
      media_presence: {
        media_appearances: [],
        press_coverage: {
          positive: 8,
          neutral: 2,
          negative: 0,
        },
        search_volume: 3,
        media_perception:
          "Positive - recognized as a standout performer in D3 baseball",
      },
    },
    current_nil_portfolio: {
      existing_deals: [],
      total_nil_earnings: 0,
      deal_execution_history: 0,
      category_exclusivity: [],
      entrepreneurial_ventures: [],
    },
    background_factors: {
      geographic_relevance: {
        regional_popularity: 5,
        local_market_opportunities: [
          "Providence area baseball camps/clinics",
          "Local sporting goods endorsements",
          "Community baseball training",
        ],
        home_state_influence: 3,
      },
      socioeconomic_profile: {
        background: "Unknown based on available data",
        financial_need: 5,
        financial_literacy: 5,
        long_term_goals: [
          "Professional baseball consideration",
          "Coaching potential",
        ],
      },
      cultural_identity: {
        communities_represented: [],
        languages_spoken: ["English"],
        cultural_initiatives: [],
        community_involvement: [],
      },
      personal_interests: {
        hobbies: [],
        non_athletic_talents: [],
        causes_supported: [],
        career_aspirations: [
          "Potential professional baseball career",
          "Possible coaching future",
        ],
      },
    },
    nil_potential_assessment: {
      projected_annual_nil_value: 2500,
      market_competition: {
        competing_athletes: [],
        uniqueness_factor: 8,
      },
      growth_potential: {
        athletic_trajectory:
          "Positive - consistent improvement from year to year",
        brand_development_opportunities: [
          "Pitching instruction",
          "Baseball clinic leadership",
          "Local endorsements in Providence area",
        ],
        projected_social_growth: 15,
      },
      risk_assessment: {
        behavioral_risk: 2,
        performance_risk: 3,
        reputation_risks: [],
        eligibility_risks: [],
      },
    },
    development_pathway: {
      team_profile: {
        name: "Johnson & Wales University Providence",
        location: "Providence, Rhode Island",
        level: "NCAA Division III",
        program_prestige: 6,
        national_visibility: 4,
        development_history: "",
        coaching_quality: 8,
      },
      development_resources: {
        training_facilities: null,
        coaching_staff: 7,
        competition_level: null,
        exposure_opportunities: null,
      },
      progression_timeline: {
        current_phase: "Redshirt Junior",
        next_milestone: "Senior year",
        high_school_projection: "",
        college_eligibility: "",
      },
      key_development_needs: [],
    },
    comparative_analysis: {
      peer_comparison: {
        relative_strengths: [],
        development_areas: [
          "Develop social media presence",
          "Create personal brand identity",
          "Build network with professional baseball connections",
        ],
      },
      player_comps: [],
      recommendation: {
        overall_nil_potential: 6,
        key_opportunities: [
          "Leverage conference awards for local endorsements",
          "Develop instructional content for younger pitchers",
          "Partner with local baseball facilities for training programs",
        ],
        improvement_areas: [
          "Develop social media presence",
          "Create personal brand identity",
          "Build network with professional baseball connections",
        ],
        strategic_recommendations: [
          "Focus on building instructional portfolio through camps and clinics",
          "Develop relationships with local sporting goods retailers",
          "Create content demonstrating pitching techniques",
          "Leverage academic achievements for broader marketability",
        ],
      },
    },
  },
  {
    SLAM_Score: 85,
    personal_info: {
      name: "Luke Addison",
      image: "https://asugoldenrams.com/images/2024/2/6/Addison_Luke.jpg",
      sport: "Baseball",
      position: "UTIL",
      year: "Senior",
      high_school: "Terrell Academy",
      hometown: "Albany, Georgia",
      current_team: "Albany State University",
      previous_team: null,
      family_legacy: {
        has_family_in_sports: false,
        notable_family_members: [],
        legacy_impact_factor: 0,
      },
      academic_profile: {
        gpa: 3.5,
        major: "Business Administration",
        academic_achievements: ["Dean's List 2024"],
        academic_eligibility_risk: "Low",
      },
    },
    athletic_performance: {
      physical_metrics: {
        height: "6'1\"",
        weight: 210,
        age: "",
        bats_throws: "Right/Right",
        fastball_velocity: null,
        fastball_percentile: null,
      },
      career_stats: {
        batting: {
          current_calendar_year: {
            games: 49,
            games_started: 47,
            at_bats: 176,
            runs: 42,
            hits: 68,
            doubles: 16,
            triples: 3,
            home_runs: 9,
            rbis: 48,
            batting_average: 0.386,
            on_base_percentage: 0.445,
            slugging_percentage: 0.665,
            ops: 1.11,
            walks: 24,
            strikeouts: 32,
            stolen_bases: 14,
            caught_stealing: 3,
          },
          progression: {
            batting_avg_trend: "Increasing",
            power_trend: "Consistent power with clutch performance",
            plate_discipline: "Excellent",
          },
        },
        pitching: {
          current_calendar_year: {
            games: 7,
            innings_pitched: 12.1,
            wins: 2,
            losses: 0,
            saves: 3,
            era: 2.92,
            strikeouts: 16,
            walks: 5,
            whip: 1.22,
            batting_avg_against: 0.198,
          },
          progression: {
            velocity_trend: "Stable",
            command_trend: "Improving",
          },
        },
      },
      key_performance_metrics: [
        "True utility player with ability to pitch and play multiple positions",
        "Clutch hitter in high-leverage situations",
        "Power to all fields with excellent situational hitting",
        "Versatile defensive skillset",
      ],
      national_rankings: {
        overall: null,
        position: 8,
      },
      awards_recognitions: [
        "2023 SIAC Utility Player of the Year",
        "2025 All-SIAC First Team",
        "2024 All-SIAC First Team",
        "2023 ABCA/Rawlings All-Region Second Team",
      ],
      injury_history: [],
      playing_style:
        "Versatile utility player with power bat and ability to pitch in relief roles",
      pro_potential: {
        draft_projection: "Late rounds prospect with utility value",
        professional_readiness: 7.5,
      },
    },
    marketability_metrics: {
      social_media: {
        platforms: [
          {
            platform: "Instagram",
            username: "luke_addison14",
            followers: 2600,
            engagement_rate: 8.8,
            content_quality: 7,
            posting_frequency: 6,
            audience_demographics: {
              age_18_24: 62,
              age_25_34: 28,
              regional_followers: "Georgia and Southeast primarily",
            },
          },
          {
            platform: "Twitter",
            username: "LukeAddison14",
            followers: 1800,
            engagement_rate: 6.5,
            content_quality: 7,
            posting_frequency: 5,
            audience_demographics: {
              age_18_24: 58,
              age_25_34: 32,
              regional_followers:
                "Georgia, Southeast, and HBCU fans nationally",
            },
          },
        ],
        total_social_reach: 4400,
        social_media_growth_rate: 26.5,
        social_media_valuation: 8000,
      },
      personal_brand: {
        public_persona:
          "Versatile, team-first player with local roots and strong community connections",
        media_training: true,
        public_speaking_ability: 8,
        interview_performance: 8,
        personal_narrative:
          "Local talent who stayed home to help build Albany State into a SIAC powerhouse",
        marketable_traits: [
          "Versatility (position player and pitcher)",
          "Clutch performer (game-winning hits)",
          "Local hero story",
          "Championship experience",
        ],
        brand_compatibility: [
          "Sports equipment",
          "Local businesses",
          "Baseball training academies",
          "Community outreach programs",
        ],
        existing_fanbase: {
          size: 5200,
          loyalty: 9,
          demographics: {
            primary: "Albany State University community",
            secondary: "Georgia baseball fans and HBCU baseball followers",
          },
        },
      },
      media_presence: {
        media_appearances: [
          "Albany Herald features",
          "SIAC Conference weekly spotlights",
          "Local Georgia sports broadcasts",
          "Albany community event appearances",
        ],
        press_coverage: {
          positive: 32,
          neutral: 5,
          negative: 0,
        },
        search_volume: 780,
        media_perception:
          "Local star who delivers in clutch moments with versatile skillset",
      },
    },
    current_nil_portfolio: {
      existing_deals: [
        {
          brand: "Georgia Athletic Supply",
          deal_type: "Endorsement",
          deal_value: 3200,
          term_length: "1 year",
          exclusivity: false,
          performance_metrics: {
            social_posts: 10,
            "in-person_appearances": 4,
          },
        },
        {
          brand: "Albany Youth Baseball Camp",
          deal_type: "Instruction/Appearance",
          deal_value: 2500,
          term_length: "6 months",
          exclusivity: false,
          performance_metrics: {
            camp_sessions: 8,
            promotional_content: 6,
          },
        },
        {
          brand: "Wilson Baseball Equipment",
          deal_type: "Product Sponsorship",
          deal_value: 1800,
          term_length: "1 year",
          exclusivity: true,
          performance_metrics: {
            social_posts: 8,
            testimonials: 2,
          },
        },
      ],
      total_nil_earnings: 7500,
      deal_execution_history: 96,
      category_exclusivity: ["Baseball equipment"],
      entrepreneurial_ventures: [
        "Co-founder of local youth baseball development program",
      ],
    },
    background_factors: {
      geographic_relevance: {
        regional_popularity: 9,
        local_market_opportunities: [
          "Albany businesses",
          "Georgia regional sponsors",
          "HBCU partnerships",
          "Southeast baseball academies",
        ],
        home_state_influence: 9,
      },
      socioeconomic_profile: {
        background: "Middle class",
        financial_need: 4,
        financial_literacy: 8,
        long_term_goals: [
          "Professional baseball career",
          "Sports management/business",
          "Youth baseball development in Georgia",
        ],
      },
      cultural_identity: {
        communities_represented: [
          "HBCU athletes",
          "Georgia baseball community",
        ],
        languages_spoken: ["English"],
        cultural_initiatives: ["HBCU baseball awareness program"],
        community_involvement: [
          "Youth baseball clinics",
          "Albany community service",
          "Campus leadership",
        ],
      },
      personal_interests: {
        hobbies: ["Fishing", "Golf", "Community service"],
        non_athletic_talents: ["Business leadership", "Public speaking"],
        causes_supported: ["Youth sports access", "Community development"],
        career_aspirations: [
          "Professional baseball",
          "Sports business entrepreneurship",
          "Baseball coaching",
        ],
      },
    },
    nil_potential_assessment: {
      projected_annual_nil_value: 12000,
      market_competition: {
        competing_athletes: [
          "Top SIAC baseball players",
          "Georgia-based college athletes across sports",
          "HBCU baseball standouts nationally",
        ],
        uniqueness_factor: 8,
      },
      growth_potential: {
        athletic_trajectory: "Continued excellence with championship potential",
        brand_development_opportunities: [
          "Georgia regional endorsements",
          "Baseball instruction content",
          "HBCU sports ambassador",
          "Local community partnerships",
        ],
        projected_social_growth: 40,
      },
      risk_assessment: {
        behavioral_risk: 2,
        performance_risk: 3,
        reputation_risks: [],
        eligibility_risks: [],
      },
    },
    development_pathway: {
      team_profile: {
        name: "Albany State University",
        location: "Albany, Georgia",
        level: "NCAA Division II, Southern Intercollegiate Athletic Conference",
        program_prestige: 8,
        national_visibility: 6,
        development_history:
          "Emerging SIAC powerhouse with conference championships",
        coaching_quality: 8,
      },
      development_resources: {
        training_facilities: 7,
        coaching_staff: 8,
        competition_level: 7,
        exposure_opportunities: 6,
      },
      progression_timeline: {
        current_phase: "Senior year, championship contention",
        next_milestone: "Professional opportunities exploration",
        high_school_projection: "Completed",
        college_eligibility: "Final year",
      },
      key_development_needs: [
        "Professional showcase preparation",
        "Continued versatility development",
        "Transition plan to professional baseball",
      ],
    },
    comparative_analysis: {
      peer_comparison: {
        relative_strengths: [
          "Elite versatility (position player and pitcher)",
          "Consistent performance",
          "Clutch hitting",
          "Championship experience",
        ],
        development_areas: [
          "National visibility expansion",
          "Professional readiness",
        ],
      },
      player_comps: [
        "Top D2 utility players",
        "Versatile MLB organizational players",
      ],
      recommendation: {
        overall_nil_potential: 8,
        key_opportunities: [
          "Leverage local Georgia connections",
          "Highlight utility player versatility for instruction",
          "Develop content featuring multiple position skills",
          "Target baseball equipment companies with versatility narrative",
        ],
        improvement_areas: [
          "Expand national visibility for post-college opportunities",
          "Develop more professional content production",
          "Create strategic portfolio highlighting unique versatility",
        ],
        strategic_recommendations: [
          "Focus on local Georgia market approach",
          "Create baseball instruction series highlighting versatility",
          "Partner with HBCU-focused brands",
          "Leverage championship moments for clutch performance narrative",
          "Position for professional opportunities through showcases",
        ],
      },
    },
  },
];

// Helper function to map API response to component format
const mapApiResponseToPlayerFormat = (apiPlayer) => {
  // Determine player type based on position
  let playerType = "hitter";
  if (
    apiPlayer?.position?.includes("P") ||
    apiPlayer?.position?.includes("pitcher")
  ) {
    playerType = "pitcher";
  }
  if (
    apiPlayer?.position?.includes("C") ||
    apiPlayer?.position?.toLowerCase() === "catcher"
  ) {
    playerType = "catcher";
  }

  return {
    id: apiPlayer?.id || apiPlayer?.players,
    PlayerID: apiPlayer?.id || apiPlayer?.players,
    Player: apiPlayer?.name,
    Position: apiPlayer?.position,
    playerType: playerType,
    Year: apiPlayer?.year,
    Height: apiPlayer?.height,
    Weight: apiPlayer?.weight,
    class: apiPlayer?.year,
    hometown: apiPlayer?.hometown,
    photo: apiPlayer?.image,
    stats: {
      batting: {
        avg: apiPlayer?.batting_average || 0,
        hr: apiPlayer?.home_runs || 0,
        rbi: apiPlayer?.rbis || 0,
      },
      pitching: {
        era: apiPlayer?.era || 0,
        so: apiPlayer?.strikeouts || 0,
        whip: apiPlayer?.whip || 0,
      },
      catching: apiPlayer?.position?.includes("C")
        ? {
          csPercent: apiPlayer?.caught_stealing_percentage || 35,
        }
        : null,
    },
  };
};

// Get player color theme based on position/type
export const getPlayerTheme = (player) => {
  const colorMap = {
    default: {
      primary: "blue",
      accent: "blue-600",
      light: "blue-50",
      border: "blue-100",
      hover: "blue-500",
    },
    hitter: {
      primary: "green",
      accent: "green-600",
      light: "green-50",
      border: "green-100",
      hover: "green-500",
    },
    catcher: {
      primary: "amber",
      accent: "amber-600",
      light: "amber-50",
      border: "amber-100",
      hover: "amber-500",
    },
    pitcher: {
      primary: player?.throws === "Right" ? "indigo" : "purple",
      accent: player?.throws === "Right" ? "indigo-600" : "purple-600",
      light: player?.throws === "Right" ? "indigo-50" : "purple-50",
      border: player?.throws === "Right" ? "indigo-100" : "purple-100",
      hover: player?.throws === "Right" ? "indigo-500" : "purple-500",
    },
  };

  return colorMap[player?.playerType] || colorMap.default;
};

// Get expanded position name
export const getPositionName = (positionCode) => {
  const positions = {
    C: "Catcher",
    "1B": "First Base",
    "2B": "Second Base",
    "3B": "Third Base",
    SS: "Shortstop",
    IF: "Infielder",
    OF: "Outfielder",
    LF: "Left Field",
    CF: "Center Field",
    RF: "Right Field",
    DH: "Designated Hitter",
    UT: "Utility",
    RHP: "Right-Handed Pitcher",
    LHP: "Left-Handed Pitcher",
    P: "Pitcher",
    "OF/2B": "Outfield/Second Base",
  };

  return positions[positionCode] || positionCode;
};

// PlayerCard component for the roster list view
// export const PlayerCard = ({ player, onClick, onChatClick }) => {
//   // Determine player type based on position
//   const playerType = player?.Position?.includes("P")
//     ? "pitcher"
//     : player?.Position?.includes("C")
//     ? "catcher"
//     : "hitter";

//   const theme = getPlayerTheme({ ...player, playerType });
//   const positionName = getPositionName(player?.Position);
//   const { theme: appTheme } = useTheme();
//   const isDarkMode = true; // OLED: always dark

//   return (
//     <div className="w-full max-w-md mx-auto">
//       <div
//         className={`relative rounded-xl overflow-hidden shadow-lg ${
//           "bg-gray-800"
//         } text-white`}
//       >
//         {/* Player Image Background */}
//         <div className="h-48 goldenGradient from-blue-900 to-gray-900 relative">
//           <div className="absolute inset-0 bg-black opacity-40"></div>
//           <div className="absolute bottom-0 left-0 p-4 z-10">
//             <div className="text-xs font-semibold uppercase tracking-wider mb-1 themeSecondary inline-block px-2 py-1 rounded-sm">
//               {player?.position}
//             </div>
//             <h2 className="text-2xl font-bold">{player?.name}</h2>
//             <p className="text-sm text-gray-300">
//               {player?.year} • {player?.height} • {player?.weight}
//             </p>
//           </div>
//         </div>

//         {/* Main Content */}
//         <div className="p-4">
//           {/* Stats Section */}
//           <div className="grid grid-cols-2 gap-4">
//             {playerType === "pitcher" ? (
//               <>
//                 <div
//                   className={`${
//                     "bg-gray-700"
//                   } p-3 rounded`}
//                 >
//                   <p className="text-xs text-gray-400 mb-1">ERA</p>
//                   <p className="font-semibold">
//                     {player?.era?.toFixed(2) || "0.00"}
//                   </p>
//                 </div>
//                 <div
//                   className={`${
//                     "bg-gray-700"
//                   } p-3 rounded`}
//                 >
//                   <p className="text-xs text-gray-400 mb-1">STRIKEOUTS</p>
//                   <p className="font-semibold">{player?.strikeouts || "0"}</p>
//                 </div>
//               </>
//             ) : (
//               <>
//                 <div
//                   className={`${
//                     "bg-gray-700"
//                   } p-3 rounded`}
//                 >
//                   <p className="text-xs text-gray-400 mb-1">BATTING AVG</p>
//                   <p className="font-semibold">
//                     {player?.batting_average?.toFixed(3) || ".000"}
//                   </p>
//                 </div>
//                 <div className="bg-gray-800 p-3 rounded">
//                   <p className="text-xs text-gray-400 mb-1">HOME RUNS</p>
//                   <p className="font-semibold">{player?.home_runs || "0"}</p>
//                 </div>
//               </>
//             )}
//           </div>

//           {/* Action Buttons */}
//           <div className="grid grid-cols-2 gap-3 mt-6">
//             <button
//               className="themeSecondary text-white py-2 rounded-lg font-medium hoverintoGray transition"
//               onClick={onClick}
//             >
//               View Full Profile
//             </button>
//             <button
//               onClick={(e) => {
//                 e.stopPropagation();
//                 onChatClick(player?.name);
//               }}
//               className="themeSecondary text-black py-2 rounded-lg font-medium  transition"
//             >
//               Chat with Roster
//             </button>
//           </div>
//         </div>
//       </div>
//     </div>
//   );
// };

// Filter component for roster list
// const RosterFilter = ({ onFilter, activeFilter, setActiveFilter }) => {
//   const { theme } = useTheme();
//   const isDarkMode = true; // OLED: always dark

//   const filters = [
//     { id: "all", label: "All Rosters" },
//     { id: "hitter", label: "Hitters" },
//     { id: "pitcher", label: "Pitchers" },
//     { id: "catcher", label: "Catchers" },
//   ];

//   return (
//     <div className="flex space-x-2 mb-6">
//       {filters.map((filter) => (
//         <button
//           key={filter.id}
//           onClick={() => {
//             setActiveFilter(filter.id);
//             onFilter({ type: "playerType", value: filter.id });
//           }}
//           className={`px-4 py-2 rounded-md text-sm font-medium ${
//             activeFilter === filter.id
//               ? "themeSecondary text-white"
//               : isDarkMode
//               ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
//               : "bg-gray-100 text-gray-700 hover:bg-gray-200"
//           }`}
//         >
//           {filter.label}
//         </button>
//       ))}
//     </div>
//   );
// };

// Component for position-specific filtering
const PositionFilter = ({ onFilter, activePosition, setActivePosition }) => {
  const sportCode = getSelectedSport();
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  const positionsBaseball = [
    { id: "all", label: "All Positions" },
    { id: "C", label: "Catcher" },
    { id: "1B", label: "First Base" },
    { id: "2B", label: "Second Base" },
    { id: "3B", label: "Third Base" },
    { id: "SS", label: "Shortstop" },
    { id: "OF", label: "Outfielder" },
    { id: "LF", label: "Left Field" },
    { id: "CF", label: "Center Field" },
    { id: "RF", label: "Right Field" },
    { id: "DH", label: "DH" },
    { id: "RHP", label: "RH Pitcher" },
    { id: "LHP", label: "LH Pitcher" },
  ];

  const positionsMBB = [
    { id: "all", label: "All Positions" },
    { id: "ATH", label: "Athlete" },
    { id: "C", label: "Center" },
    { id: "F", label: "Forward" },
    { id: "G", label: "Guard" },
    { id: "PF", label: "Power Forward" },
    { id: "PG", label: "Point Guard" },
    { id: "SF", label: "Small Forward" },
    { id: "SG", label: "Shooting Guard" },
  ];

  // Check if it's a baseball sport code (MLB, CBB, MILB, etc.)
  const isBaseballSport = ['CBB', 'MLB', 'MILB', 'NPB', 'KBO'].includes(sportCode?.toUpperCase());
  const positions = isBaseballSport ? positionsBaseball : positionsMBB;

  return (
    <div className="mb-6">
      <h3
        className={`text-sm font-medium ${"text-gray-300"
          } mb-2`}
      >
        Filter by Position:
      </h3>
      <div className="flex flex-wrap gap-2">
        {positions.map((position) => (
          <button
            key={position.id}
            onClick={() => {
              setActivePosition(position.id);
              onFilter({ type: "position", value: position.id });
            }}
            className={`px-3 py-1 rounded-full text-xs font-medium ${activePosition === position.id
              ? "themePrimary text-white"
              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
              }`}
          >
            {position.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// Utility function to map the new player data format to the expected format
const mapNewPlayerFormat = (playerData) => {
  // Check if the data is already in the old format
  if (!playerData.personal_info && !playerData.athletic_performance) {
    return playerData; // Already in correct format
  }

  // Determine player type based on position
  let playerType = "hitter";
  if (
    playerData.personal_info.position.includes("RHP") ||
    playerData.personal_info.position.includes("LHP") ||
    playerData.personal_info.position.includes("P") ||
    playerData.personal_info.position.includes("pitcher")
  ) {
    playerType = "pitcher";
  }
  if (
    playerData.personal_info.position.includes("C") ||
    playerData.personal_info.position.includes("catcher") ||
    playerData.personal_info.position.toLowerCase() === "catcher"
  ) {
    playerType = "catcher";
  }

  // Get primary position
  const position = playerData.personal_info.position.split(",")[0].trim();

  // Extract physical metrics
  const physical = playerData.athletic_performance.physical_metrics || {};

  // Get the right calendar year for stats
  const battingStatsKey = getBattingStatsKey(playerData);
  const pitchingStatsKey = getPitchingStatsKey(playerData);

  // Extract batting stats
  const battingStats = battingStatsKey
    ? playerData.athletic_performance.career_stats.batting[battingStatsKey] ||
    {}
    : {};

  // Create highlights from key metrics or performance data
  const highlights = [];

  // Add SLAM score if available (prioritize this)
  if (playerData.SLAM_Score) {
    highlights.push({
      metric: "SLAM Score",
      value: playerData.SLAM_Score.toString(),
      rating: playerData.SLAM_Score,
    });
  }

  // Add batting metric if available
  if (battingStats.batting_average) {
    highlights.push({
      metric: "Batting Average",
      value: battingStats.batting_average.toFixed(3),
      rating: Math.round(battingStats.batting_average * 100),
    });
  }

  // Add OBP if available
  if (battingStats.on_base_percentage) {
    highlights.push({
      metric: "On-Base %",
      value: battingStats.on_base_percentage.toFixed(3),
      rating: Math.round(battingStats.on_base_percentage * 80), // Adjusted to be less than 100
    });
  }

  // Add stolen bases metric if available
  if (battingStats.stolen_bases) {
    const successRate =
      (battingStats.stolen_bases /
        (battingStats.stolen_bases + (battingStats.caught_stealing || 0))) *
      100;

    highlights.push({
      metric: "Stolen Base Success",
      value: `${Math.round(successRate)}%`,
      rating: Math.round(successRate),
    });
  }

  // Add power metrics if available
  if (battingStats.home_runs) {
    highlights.push({
      metric: "Home Runs",
      value: battingStats.home_runs.toString(),
      rating: Math.min(100, battingStats.home_runs * 8), // Scale up to make visual sense
    });
  }

  // Add slugging if available
  if (battingStats.slugging_percentage) {
    highlights.push({
      metric: "Slugging %",
      value: battingStats.slugging_percentage.toFixed(3),
      rating: Math.round(battingStats.slugging_percentage * 70), // Adjusted scale
    });
  }

  // Add pitching metric if pitcher and stats available
  const pitchingStats =
    playerData.athletic_performance.career_stats.pitching[pitchingStatsKey];
  if (playerType === "pitcher" && pitchingStatsKey) {
    if (physical.fastball_velocity) {
      highlights.push({
        metric: "Fastball Velocity",
        value: `${physical.fastball_velocity} MPH`,
        rating: physical.fastball_percentile || 50,
      });
    }

    if (
      pitchingStats &&
      pitchingStats.era !== undefined &&
      pitchingStats.era !== null
    ) {
      // Lower ERA is better, so we invert the rating (e.g., 3.00 ERA might be 70% rating)
      const eraRating = Math.max(0, 100 - pitchingStats.era * 10);
      highlights.push({
        metric: "ERA",
        value: pitchingStats.era.toFixed(2),
        rating: eraRating,
      });
    }
  }

  // Add catcher metrics if available
  if (playerType === "catcher") {
    // Check if there's any catching-specific stats in progression
    const progression =
      playerData.athletic_performance.career_stats.batting.progression;
    if (progression) {
      // Try to parse pop time from stats
      const popTimeMatch = JSON.stringify(progression).match(
        /(\d+\.\d+)(-| )second pop time/i
      );
      if (popTimeMatch) {
        const popTime = parseFloat(popTimeMatch[1]);
        // 1.9s is elite (95 rating), 2.1s is average (50 rating)
        const popTimeRating = Math.max(0, 100 - (popTime - 1.8) * 100);
        highlights.push({
          metric: "Pop Time",
          value: `${popTime}s`,
          rating: Math.round(popTimeRating),
        });
      }

      // Parse other catcher metrics if available
      if (progression.caught_stealing_percentage) {
        const csPercent = parseInt(progression.caught_stealing_percentage);
        highlights.push({
          metric: "CS%",
          value: `${csPercent}%`,
          rating: csPercent,
        });
      }
    }
  }

  // Extract highlights from key_performance_metrics if available
  if (
    playerData.athletic_performance.key_performance_metrics &&
    playerData.athletic_performance.key_performance_metrics.length > 0 &&
    highlights.length < 3
  ) {
    // Try to extract meaningful metrics from the key_performance_metrics text
    const metrics = playerData.athletic_performance.key_performance_metrics;

    // Look for exit velocity
    const exitVelocityMatch = JSON.stringify(metrics).match(
      /Exit velocity: (\d+) mph/i
    );
    if (exitVelocityMatch && highlights.length < 3) {
      const velocity = parseInt(exitVelocityMatch[1]);
      // Scale: 95+ mph is elite (90+ rating), 85 mph is average (50 rating)
      const velocityRating = Math.max(40, Math.min(95, (velocity - 75) * 2));
      highlights.push({
        metric: "Exit Velocity",
        value: `${velocity} MPH`,
        rating: Math.round(velocityRating),
      });
    }

    // Look for walk rate
    const walkRateMatch = JSON.stringify(metrics).match(
      /Walk rate: (\d+\.\d+)%/i
    );
    if (walkRateMatch && highlights.length < 3) {
      const walkRate = parseFloat(walkRateMatch[1]);
      // Scale: 15% is elite (90 rating), 8% is average (50 rating)
      const walkRating = Math.max(20, Math.min(95, 30 + walkRate * 4));
      highlights.push({
        metric: "Walk Rate",
        value: `${walkRate}%`,
        rating: Math.round(walkRating),
      });
    }
  }

  // Add NIL potential if available and we need more highlights
  let nilValue = playerData.nil_potential_assessment.projected_annual_nil_value;
  if (
    highlights.length < 3 &&
    playerData.nil_potential_assessment &&
    playerData.nil_potential_assessment.projected_annual_nil_value
  ) {
    // Handle different formats of NIL value

    // If it's a range like "$5,000-15,000", extract the higher number
    if (typeof nilValue === "string" && nilValue.includes("-")) {
      const matches = nilValue.match(/\$?([\d,]+)-\$?([\d,]+)/);
      if (matches) {
        nilValue = parseInt(matches[2].replace(/,/g, ""));
      } else {
        nilValue = 5000; // Default if can't parse
      }
    }

    highlights.push({
      metric: "NIL Potential",
      value: typeof nilValue === "string" ? nilValue : `${nilValue}`,
      rating: Math.min(95, typeof nilValue === "number" ? nilValue / 200 : 75), // Scale for visual appeal
    });
  }

  // Add national ranking if available
  if (
    playerData.athletic_performance.national_rankings &&
    playerData.athletic_performance.national_rankings.position &&
    highlights.length < 3
  ) {
    const posRank = playerData.athletic_performance.national_rankings.position;
    highlights.push({
      metric: "Position Rank",
      value: `#${posRank}`,
      rating: Math.max(20, 100 - posRank * 2), // Lower rank = higher rating
    });
  }

  // Ensure we have at least 3 highlights
  while (highlights.length < 3) {
    // Add generic highlight if we still need more
    highlights.push({
      metric: "Development Potential",
      value: "High",
      rating: 75,
    });
  }

  // Take just the top 3-5 highlights
  if (highlights.length > 5) {
    highlights.splice(5);
  }

  // Set default values for missing physical metrics
  const height = physical.height || "6'0\"";
  const weightVal = physical.weight || 180;
  const weight = weightVal ? `${weightVal} lbs` : "180 lbs";

  // Get batting/throwing hands
  let bats = "",
    throws = "";
  if (physical.bats_throws) {
    const parts = physical.bats_throws.split("/");
    bats = parts[0];
    throws = parts.length > 1 ? parts[1] : "";
  }

  // Construct the mapped player object
  const mappedPlayer = {
    id: playerData.personal_info.name.toLowerCase().replace(/\s+/g, "-"),
    name: playerData.personal_info.name,
    position: position,
    playerType: playerType,
    slamScore: playerData.SLAM_Score.toString(),
    nilPotential: nilValue,
    era: pitchingStats?.era?.toFixed(2) || 0.0,
    jersey: "", // Not available in new format
    class: playerData.personal_info.year,
    hometown: playerData.personal_info.hometown,
    height: height,
    weight: weight,
    bats: bats,
    throws: throws,
    previousSchool: playerData.personal_info.previous_team || "",
    teamColor: playerData.personal_info.teamColor || "green", // Default
    photo: playerData.personal_info.image || playerData.personal_info.photo,
    stats: {
      batting: {
        avg: battingStats.batting_average || 0,
        ops:
          battingStats.ops ||
          (battingStats.on_base_percentage || 0) +
          (battingStats.slugging_percentage || 0),
        hits: battingStats.hits || 0,
        hr: battingStats.home_runs || 0,
        rbi: battingStats.rbis || 0,
        slg: battingStats.slugging_percentage || 0,
        obp: battingStats.on_base_percentage || 0,
      },
    },
    highlights: highlights,
  };

  // Add pitching stats if available
  if (pitchingStatsKey) {
    const pitchingData =
      playerData.athletic_performance.career_stats.pitching[pitchingStatsKey] ||
      {};

    mappedPlayer.stats.pitching = {
      era: pitchingData.era || 0,
      wins: pitchingData.wins || 0,
      losses: pitchingData.losses || 0,
      saves: pitchingData.saves || 0,
      ip: pitchingData.innings_pitched || 0,
      so: pitchingData.strikeouts || 0,
      bb: pitchingData.walks || 0,
      whip: pitchingData.whip || 0,
      kper9:
        pitchingData.strikeouts && pitchingData.innings_pitched
          ? (pitchingData.strikeouts * 9) / pitchingData.innings_pitched
          : 0,
    };
  }

  // Add catching stats if available
  if (playerType === "catcher") {
    // Try to extract caught stealing percentage from text data
    const csText = JSON.stringify(playerData);
    const csMatch = csText.match(
      /CS%: (\d+)%|caught stealing percentage: (\d+)|CS: (\d+)%/i
    );

    mappedPlayer.stats.catching = {
      csPercent: csMatch
        ? parseInt(csMatch[1] || csMatch[2] || csMatch[3])
        : 35, // Default value
    };
  }

  return mappedPlayer;
};

// Helper function to get the correct batting stats key from different formats
const getBattingStatsKey = (playerData) => {
  if (
    !playerData.athletic_performance ||
    !playerData.athletic_performance.career_stats ||
    !playerData.athletic_performance.career_stats.batting
  ) {
    return null;
  }

  const battingData = playerData.athletic_performance.career_stats.batting;

  // Check for different possible keys
  if (battingData["2025_calendar_year"]) {
    return "2025_calendar_year";
  } else if (battingData["current_calendar_year"]) {
    return "current_calendar_year";
  } else {
    // If no standard key found, return the first key that exists
    const keys = Object.keys(battingData).filter(
      (k) =>
        typeof battingData[k] === "object" &&
        battingData[k] !== null &&
        !k.includes("progression")
    );

    return keys.length > 0 ? keys[0] : null;
  }
};

// Helper function to get the correct pitching stats key
const getPitchingStatsKey = (playerData) => {
  if (
    !playerData.athletic_performance ||
    !playerData.athletic_performance.career_stats ||
    !playerData.athletic_performance.career_stats.pitching
  ) {
    return null;
  }

  const pitchingData = playerData.athletic_performance.career_stats.pitching;

  // Check for different possible keys
  if (pitchingData["2025_calendar_year"]) {
    return "2025_calendar_year";
  } else if (pitchingData["current_calendar_year"]) {
    return "current_calendar_year";
  } else {
    // If no standard key found, return the first key that exists
    const keys = Object.keys(pitchingData).filter(
      (k) =>
        typeof pitchingData[k] === "object" &&
        pitchingData[k] !== null &&
        !k.includes("progression")
    );

    return keys.length > 0 ? keys[0] : null;
  }
};

// Main PlayerRoster component
const PlayerRoster = () => {
  const navigate = useNavigate();
  const [teamFilter, setTeamFilter] = useState(true);
  const [players, setPlayers] = useState(
    !teamFilter
      ? samplePlayers?.map((player) => mapNewPlayerFormat(player))
      : []
  );
  const [filteredPlayers, setFilteredPlayers] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");
  const [activePosition, setActivePosition] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useState("list"); // 'grid' or 'list'
  const [activeChatPlayer, setActiveChatPlayer] = useState(null);
  const { theme } = useTheme();
  const isDarkMode = true; // OLED: always dark

  useEffect(() => {
    if (teamFilter) {
      fetchPlayers();
    } else {
      const processedPlayers = samplePlayers?.map((player) =>
        mapNewPlayerFormat(player)
      );
      console.log(processedPlayers);
      setFilteredPlayers(processedPlayers);
      setPlayers(samplePlayers);
    }
  }, [teamFilter]);

  const fetchPlayers = async () => {
    try {
      const savedTeam = sessionStorage.getItem("selectedTeam");
      const team = savedTeam ? JSON.parse(savedTeam) : null;

      if (!team?.team_id) {
        console.warn("No team selected");
        return;
      }
      let response
      if (getSelectedSport() == 'MBB') {
        response = await apiClient.get(`https://slam-all-python-359065791766.us-central1.run.app/${getSelectedSport()}/tp-players/`, {
          params: {
            team: team.name,
            skip: 0,
            schema: getSelectedSport(),
            limit: 100
          }
        });
        response = response?.data
      }
      else {
        response = await apiClient.get(`${getSelectedSport()}/players/`, {
          params: {
            team_id: team.team_id,
            skip: 0,
            limit: 100
          }
        });
      }
      const mappedPlayers = (response?.data || []).map(
        mapApiResponseToPlayerFormat
      );
      setPlayers(mappedPlayers);
      setFilteredPlayers(mappedPlayers);
    } catch (error) {
      console.error("Error fetching players:", error);
    }
  };
  // Apply all active filters
  const applyFilters = () => {
    let results = [...players];

    // Apply player type filter
    if (activeFilter !== "all") {
      results = results.filter((player) => player?.playerType === activeFilter);
    }

    // Apply position filter
    if (activePosition !== "all") {
      results = results.filter((player) =>
        player?.position?.includes(activePosition)
      );
    }

    // Apply search query
    if (searchQuery) {
      results = results.filter(
        (player) =>
          player?.Player?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          player?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          player?.position?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          player?.class?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          player?.hometown?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          getPositionName(player?.position)
            ?.toLowerCase()
            .includes(searchQuery.toLowerCase())
      );
    }

    setFilteredPlayers(results);
  };

  // Handle filter changes
  const handleFilter = (filter) => {
    if (filter.type === "playerType") {
      setActiveFilter(filter.value);
    } else if (filter.type === "position") {
      setActivePosition(filter.value);
    }
  };

  // Apply filters whenever the active filters change
  useEffect(() => {
    applyFilters();
  }, [activeFilter, activePosition, searchQuery]);

  // Handle search
  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
  };

  return (
    <div
      className={`container mx-auto p-4 ${"bg-[#0a0a0a] text-gray-100"
        }`}
    >
      <div className="flex items-center mb-4">
        <img src="/SLAM_logo.png" alt="UNC" className="h-16 mr-4" />
        <div>
          <h1
            className={`text-2xl font-bold ${"text-gray-100"
              }`}
          >
            {getSportTitle()}
          </h1>
          <p className={`${"text-gray-300"}`}>
            View comprehensive roster profiles with position-specific analytics
            and metrics
          </p>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
          <div className="relative w-full md:w-64">
            <input
              type="text"
              placeholder="Search rosters..."
              value={searchQuery}
              onChange={handleSearch}
              className={`w-full pl-10 pr-4 py-2 rounded-lg ${"bg-gray-800 border-gray-700 text-gray-200 focus:ring-blue-400"
                } border focus:outline-none focus:ring-2`}
            />
            <Search
              className={`absolute left-3 top-2.5 ${"text-gray-400"
                }`}
              size={18}
            />
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 rounded-lg ${viewMode === "grid"
                ? "bg-blue-900 text-blue-300"
                : "text-gray-300 hover:bg-gray-800"
                }`}
            >
              <GridIcon size={20} />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 rounded-lg ${viewMode === "list"
                ? "bg-blue-900 text-blue-300"
                : "text-gray-300 hover:bg-gray-800"
                }`}
            >
              <List size={20} />
            </button>
            <div
              className={`border-l ${"border-gray-700"
                } h-8 mx-2`}
            ></div>
            <button
              className={`flex items-center ${"text-gray-300 hover:text-blue-400"
                }`}
            >
              <SortAsc size={18} className="mr-1" />
              <span className="text-sm">Sort</span>
            </button>
            <button
              className={`flex items-center ${"text-gray-300 hover:text-blue-400"
                }`}
            >
              <Filter size={18} className="mr-1" />
              <span className="text-sm">Filter</span>
            </button>
          </div>
        </div>

        <div className="flex flex-col space-y-4">
          {/* <RosterFilter
            onFilter={handleFilter}
            activeFilter={activeFilter}
            setActiveFilter={setActiveFilter}
          /> */}
          <PositionFilter
            onFilter={handleFilter}
            activePosition={activePosition}
            setActivePosition={setActivePosition}
          />
        </div>
      </div>

      {/* Team Filter Toggle */}
      {/* <TeamRosterToggle searchQuery={searchQuery} setTeamFilter={setTeamFilter} teamFilter={teamFilter} /> */}

      {/* Players Grid */}
      {viewMode === "grid" && (
        <>
          <TeamRosterToggle
            viewMode={viewMode}
            searchQuery={searchQuery}
            setTeamFilter={setTeamFilter}
            teamFilter={teamFilter}
          />
        </>
      )}

      {/* Players List View */}
      {viewMode === "list" && (
        <>
          <TeamRosterToggle
            viewMode={viewMode}
            searchQuery={searchQuery}
            setTeamFilter={setTeamFilter}
            teamFilter={teamFilter}
          />
        </>
      )}

      {/* Player Chatbot - Only shows when a player is selected */}
      {activeChatPlayer && (
        <PlayerChatbot
          key={activeChatPlayer}
          setActiveChatPlayer={setActiveChatPlayer}
          name={activeChatPlayer}
          defaultOpened={true}
        />
      )}
    </div>
  );
};

export default PlayerRoster;